import React, { useEffect, useMemo, useState, useCallback } from "react";
import { http, formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { useConfirm } from "../components/ConfirmDialog";

const STATUS_PILL = {
  pending:    { bg: "rgba(184,138,58,0.15)", fg: "#8a6420", label: "Pending" },
  approved:   { bg: "rgba(79,122,74,0.15)", fg: "#3a5a36", label: "Approved" },
  rejected:   { bg: "rgba(184,90,61,0.18)", fg: "#8a3c20", label: "Rejected" },
  claimed:    { bg: "rgba(74,111,138,0.15)", fg: "#345469", label: "Claimed" },
  active:     { bg: "rgba(79,122,74,0.15)", fg: "#3a5a36", label: "Active" },
  suspended:  { bg: "rgba(184,90,61,0.18)", fg: "#8a3c20", label: "Suspended" },
};

function Pill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.pending;
  return (
    <span
      style={{
        background: s.bg, color: s.fg, fontSize: 10.5, fontFamily: "var(--font-mono)",
        letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 8px",
        borderRadius: 999, fontWeight: 600,
      }}
      data-testid={`pill-${status}`}
    >
      {s.label}
    </span>
  );
}

function StatTile({ label, value, hint }) {
  return (
    <div className="card" style={{ padding: "16px 18px", minWidth: 0 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 600, lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("waitlist"); // waitlist | users
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState("");

  const loadStats = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/stats");
      setStats(data);
    } catch (e) {
      setStatsErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  if (user?.role !== "admin") {
    return (
      <div data-testid="admin-forbidden" className="empty-state" style={{ marginTop: 40 }}>
        Admin access required.
      </div>
    );
  }

  return (
    <div data-testid="admin-view" style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 4 }}>
            Vidhya · admin console
          </div>
          <h1 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 600, margin: 0 }}>
            Members & access
          </h1>
        </div>
      </div>

      {statsErr && <div className="auth-error" style={{ marginBottom: 16 }}>{statsErr}</div>}

      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 12, marginBottom: 22 }}>
          <StatTile label="Pending waitlist" value={stats.waitlist.pending} hint="awaiting your decision" />
          <StatTile label="Approved" value={stats.waitlist.approved} hint="not yet signed up" />
          <StatTile label="Active users" value={stats.users.active} />
          <StatTile label="Suspended" value={stats.users.suspended} hint="login blocked" />
        </div>
      )}

      <div className="row" style={{ gap: 6, marginBottom: 14 }}>
        <button
          className={`btn btn-sm ${tab === "waitlist" ? "btn-clay" : "btn-ghost"}`}
          onClick={() => setTab("waitlist")}
          data-testid="admin-tab-waitlist"
          style={tab === "waitlist" ? { boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.15)" } : { opacity: 0.65 }}
        >
          Waitlist
          {stats && <span className="pill" style={{ marginLeft: 6 }}>{stats.waitlist.pending}</span>}
        </button>
        <button
          className={`btn btn-sm ${tab === "users" ? "btn-clay" : "btn-ghost"}`}
          onClick={() => setTab("users")}
          data-testid="admin-tab-users"
          style={tab === "users" ? { boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.15)" } : { opacity: 0.65 }}
        >
          Users
          {stats && <span className="pill" style={{ marginLeft: 6 }}>{stats.users.total}</span>}
        </button>
      </div>

      {tab === "waitlist" && <WaitlistTable refreshStats={loadStats} />}
      {tab === "users" && <UsersTable currentUserId={user?.id} refreshStats={loadStats} />}
    </div>
  );
}

function WaitlistTable({ refreshStats }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionId, setActionId] = useState("");
  const [err, setErr] = useState("");
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await http.get(`/admin/waitlist?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const act = async (wid, action) => {
    setActionId(wid + ":" + action); setErr("");
    try {
      if (action === "approve") await http.post(`/admin/waitlist/${wid}/approve`);
      else if (action === "reject") await http.post(`/admin/waitlist/${wid}/reject`);
      else if (action === "delete") {
        const ok = await confirm({
          title: "Delete waitlist entry?",
          message: "This will permanently remove the entry. The person can join the waitlist again later.",
          confirmLabel: "Delete",
          danger: true,
        });
        if (!ok) { setActionId(""); return; }
        await http.delete(`/admin/waitlist/${wid}`);
      }
      await load();
      refreshStats();
    } catch (e) { setErr(formatApiError(e)); } finally { setActionId(""); }
  };

  return (
    <div className="card" data-testid="admin-waitlist-card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: 14, gap: 8, borderBottom: "1px solid var(--bg-sunk)", flexWrap: "wrap" }}>
        <input
          className="input"
          style={{ flex: "1 1 220px" }}
          placeholder="Search email, name, country, major…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-waitlist-search"
        />
        <select
          className="input"
          style={{ width: 160 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-waitlist-filter"
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="claimed">Claimed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      {err && <div className="auth-error" style={{ margin: 14 }}>{err}</div>}
      <div style={{ overflowX: "auto" }}>
        <table className="admin-table" data-testid="admin-waitlist-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Country / grade</th>
              <th>Intended major</th>
              <th>Joined</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} data-testid={`waitlist-row-${w.id}`}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{w.name || "—"}</div>
                  <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{w.email}</div>
                </td>
                <td style={{ fontSize: 12.5 }}>
                  <div>{w.country || "—"}</div>
                  <div style={{ color: "var(--ink-3)" }}>{w.grade_level || ""}</div>
                </td>
                <td style={{ fontSize: 12.5 }}>{w.intended_major || "—"}</td>
                <td style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
                  {w.created_at ? new Date(w.created_at).toLocaleDateString() : ""}
                </td>
                <td><Pill status={w.status} /></td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {w.status === "pending" && (
                    <>
                      <button
                        className="btn btn-sm btn-clay"
                        disabled={!!actionId}
                        onClick={() => act(w.id, "approve")}
                        data-testid={`approve-${w.id}`}
                      >
                        {actionId === w.id + ":approve" ? "…" : "Approve"}
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={!!actionId}
                        onClick={() => act(w.id, "reject")}
                        data-testid={`reject-${w.id}`}
                        style={{ marginLeft: 6 }}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {w.status === "approved" && (
                    <button
                      className="btn btn-sm btn-ghost"
                      disabled={!!actionId}
                      onClick={() => act(w.id, "reject")}
                      data-testid={`revoke-${w.id}`}
                    >
                      Revoke approval
                    </button>
                  )}
                  {(w.status === "rejected" || w.status === "claimed") && (
                    <button
                      className="btn btn-sm btn-ghost"
                      disabled={!!actionId}
                      onClick={() => act(w.id, "delete")}
                      data-testid={`delete-waitlist-${w.id}`}
                      style={{ color: "#8a3c20" }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && !busy && (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 28, color: "var(--ink-3)" }}>No matching waitlist entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTable({ currentUserId, refreshStats }) {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionId, setActionId] = useState("");
  const [err, setErr] = useState("");
  const [detailUid, setDetailUid] = useState("");
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (roleFilter) params.set("role", roleFilter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await http.get(`/admin/users?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  }, [statusFilter, roleFilter, q]);

  useEffect(() => { load(); }, [load]);

  const act = async (uid, action, payload) => {
    setActionId(uid + ":" + action); setErr("");
    try {
      if (action === "suspend") await http.post(`/admin/users/${uid}/suspend`);
      else if (action === "reactivate") await http.post(`/admin/users/${uid}/reactivate`);
      else if (action === "promote") await http.post(`/admin/users/${uid}/promote`, payload);
      else if (action === "plan") await http.post(`/admin/users/${uid}/plan`, payload);
      else if (action === "delete") {
        const ok = await confirm({
          title: "Permanently delete this user?",
          message: "Their account, universities, tasks, scholarships and email drafts will all be erased. This cannot be undone.",
          confirmLabel: "Delete user",
          danger: true,
        });
        if (!ok) { setActionId(""); return; }
        await http.delete(`/admin/users/${uid}`);
      }
      await load();
      refreshStats();
    } catch (e) { setErr(formatApiError(e)); } finally { setActionId(""); }
  };

  const visible = useMemo(() => items, [items]);

  return (
    <div className="card" data-testid="admin-users-card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: 14, gap: 8, borderBottom: "1px solid var(--bg-sunk)", flexWrap: "wrap" }}>
        <input
          className="input"
          style={{ flex: "1 1 220px" }}
          placeholder="Search by email or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-users-search"
        />
        <select
          className="input"
          style={{ width: 150 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="admin-users-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select
          className="input"
          style={{ width: 130 }}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          data-testid="admin-users-role-filter"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
      </div>
      {err && <div className="auth-error" style={{ margin: 14 }}>{err}</div>}
      <div style={{ overflowX: "auto" }}>
        <table className="admin-table" data-testid="admin-users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Last login</th>
              <th>Logins</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => {
              const isSelf = u.id === currentUserId;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 13.5, cursor: "pointer" }} onClick={() => setDetailUid(u.id)} data-testid={`user-name-${u.id}`}>
                      {u.name}
                      {isSelf && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--ink-3)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}>· you</span>}
                    </div>
                    <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{u.email}</div>
                  </td>
                  <td>
                    <span style={{
                      fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: u.role === "admin" ? "#8a3c20" : "var(--ink-3)",
                      fontWeight: 600,
                    }}>{u.role}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`btn btn-sm ${u.plan === "navigator" ? "btn-clay" : "btn-ghost"}`}
                      disabled={!!actionId}
                      onClick={() => act(u.id, "plan", { plan: u.plan === "navigator" ? "explorer" : "navigator" })}
                      data-testid={`plan-toggle-${u.id}`}
                      title={u.plan === "navigator" ? "Click to downgrade to Explorer" : "Click to upgrade to Navigator"}
                      style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase" }}
                    >
                      {u.plan === "navigator" ? "Navigator" : "Explorer"}
                    </button>
                  </td>
                  <td><Pill status={u.status || "active"} /></td>
                  <td style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
                  </td>
                  <td style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                  </td>
                  <td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{u.login_count || 0}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setDetailUid(u.id)}
                      data-testid={`view-user-${u.id}`}
                      style={{ marginRight: 6 }}
                    >
                      View
                    </button>
                    {!isSelf && u.status !== "suspended" && (
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={!!actionId}
                        onClick={() => act(u.id, "suspend")}
                        data-testid={`suspend-${u.id}`}
                        style={{ color: "#8a3c20" }}
                      >
                        Suspend
                      </button>
                    )}
                    {!isSelf && u.status === "suspended" && (
                      <button
                        className="btn btn-sm btn-clay"
                        disabled={!!actionId}
                        onClick={() => act(u.id, "reactivate")}
                        data-testid={`reactivate-${u.id}`}
                      >
                        Reactivate
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={!!actionId}
                        onClick={() => act(u.id, "promote", { role: u.role === "admin" ? "user" : "admin" })}
                        data-testid={`promote-${u.id}`}
                        style={{ marginLeft: 6 }}
                      >
                        {u.role === "admin" ? "Demote" : "Make admin"}
                      </button>
                    )}
                    {!isSelf && (
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={!!actionId}
                        onClick={() => act(u.id, "delete")}
                        data-testid={`delete-user-${u.id}`}
                        style={{ marginLeft: 6, color: "#8a3c20" }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && !busy && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 28, color: "var(--ink-3)" }}>No users match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {detailUid && (
        <UserDetailDrawer
          uid={detailUid}
          currentUserId={currentUserId}
          actionId={actionId}
          onAction={act}
          onClose={() => setDetailUid("")}
        />
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0", borderBottom: "1px solid var(--bg-sunk)" }}>
      <span style={{ fontSize: 12, color: "var(--ink-3)", fontFamily: "var(--font-mono)", letterSpacing: "0.04em" }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 500, textAlign: "right" }}>{Array.isArray(value) ? value.join(", ") : String(value)}</span>
    </div>
  );
}

function UserDetailDrawer({ uid, currentUserId, actionId, onAction, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr("");
    http.get(`/admin/users/${uid}/detail`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(formatApiError(e)); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [uid]);

  const u = data?.user || {};
  const p = data?.profile || {};
  const c = data?.counts || {};
  const isSelf = uid === currentUserId;
  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  const doAction = async (action, payload) => {
    await onAction(uid, action, payload);
    if (action === "delete") onClose();
  };

  return (
    <div
      onClick={onClose}
      data-testid="user-detail-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(20,16,12,0.42)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="user-detail-drawer"
        className="card"
        style={{ width: "min(440px, 92vw)", height: "100%", borderRadius: 0, overflowY: "auto", padding: 24, boxShadow: "-12px 0 40px rgba(0,0,0,0.16)" }}
      >
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 4 }}>User detail</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 600 }}>{u.name || "—"}</div>
            <div style={{ color: "var(--ink-3)", fontSize: 12.5 }}>{u.email}</div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="user-detail-close">✕</button>
        </div>

        {busy && <div style={{ color: "var(--ink-3)", fontSize: 13 }}>Loading…</div>}
        {err && <div className="auth-error">{err}</div>}

        {data && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, marginBottom: 20 }}>
              <StatTile label="Universities" value={c.universities ?? 0} />
              <StatTile label="Tasks" value={c.tasks ?? 0} />
              <StatTile label="Scholarships" value={c.scholarships ?? 0} />
              <StatTile label="Email drafts" value={c.emails ?? 0} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6 }}>Account</div>
              <DetailRow label="Role" value={u.role} />
              <DetailRow label="Plan" value={u.plan} />
              <DetailRow label="Status" value={u.status || "active"} />
              <DetailRow label="Joined" value={fmt(u.created_at)} />
              <DetailRow label="Last login" value={fmt(u.last_login_at)} />
              <DetailRow label="Login count" value={u.login_count ?? 0} />
              <DetailRow label="User ID" value={u.id} />
            </div>

            <div style={{ marginBottom: 22 }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6 }}>Profile</div>
              <DetailRow label="Intake term" value={p.intake_term} />
              <DetailRow label="Degree level" value={p.intended_degree_level || p.intended_level} />
              <DetailRow label="Intended major" value={p.intended_major} />
              <DetailRow label="Grade level" value={p.grade_level} />
              <DetailRow label="GPA" value={p.gpa} />
              <DetailRow label="Predicted grades" value={p.predicted_grades} />
              <DetailRow label="Target countries" value={p.target_countries} />
              <DetailRow label="Citizenship" value={p.citizenship || p.country_of_citizenship} />
              <DetailRow label="Student country" value={p.student_country} />
              <DetailRow label="Budget" value={p.budget} />
              <DetailRow label="Funding need" value={p.funding_need} />
              <DetailRow label="English test" value={p.english_test} />
              <DetailRow label="English score" value={p.english_score} />
              <DetailRow label="SAT" value={p.sat_score} />
              <DetailRow label="ACT" value={p.act_score} />
              {!p.intake_term && !p.intended_major && !p.gpa && (
                <div style={{ fontSize: 12.5, color: "var(--ink-3)", padding: "8px 0" }}>No profile details yet.</div>
              )}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button
                className={`btn btn-sm ${u.plan === "navigator" ? "btn-clay" : "btn-ghost"}`}
                disabled={!!actionId}
                onClick={() => doAction("plan", { plan: u.plan === "navigator" ? "explorer" : "navigator" })}
                data-testid="detail-plan-toggle"
              >
                {u.plan === "navigator" ? "Downgrade → Explorer" : "Upgrade → Navigator"}
              </button>
              {!isSelf && (
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={!!actionId}
                  onClick={() => doAction("promote", { role: u.role === "admin" ? "user" : "admin" })}
                  data-testid="detail-promote"
                >
                  {u.role === "admin" ? "Demote to user" : "Make admin"}
                </button>
              )}
              {!isSelf && u.status !== "suspended" && (
                <button className="btn btn-sm btn-ghost" disabled={!!actionId} onClick={() => doAction("suspend")} data-testid="detail-suspend" style={{ color: "#8a3c20" }}>Suspend</button>
              )}
              {!isSelf && u.status === "suspended" && (
                <button className="btn btn-sm btn-clay" disabled={!!actionId} onClick={() => doAction("reactivate")} data-testid="detail-reactivate">Reactivate</button>
              )}
              {!isSelf && (
                <button className="btn btn-sm btn-ghost" disabled={!!actionId} onClick={() => doAction("delete")} data-testid="detail-delete" style={{ color: "#8a3c20" }}>Delete user</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
